import type { QuickSearchHierarchyResult } from '@/types/searchTypes'
import clsx from 'clsx'
import { useEffect } from 'react'
import { useOverflowList } from 'react-responsive-overflow-list'
import { EmptyState } from './EmptyState'
import { HierarchyItem } from './HierarchyItem'

interface ListSectionProps {
  query: string
  results: QuickSearchHierarchyResult[]
  onResultSelect: (result: QuickSearchHierarchyResult) => void
  displayMode?: 'default' | 'inline-pipe'
  displayType?: boolean
  enableOverflow?: boolean
  onOverflowCountChange?: (hiddenCount: number | null) => void
}

export function ListSection({
  query,
  results,
  onResultSelect,
  displayMode = 'default',
  displayType = false,
  enableOverflow = false,
  onOverflowCountChange,
}: Readonly<ListSectionProps>) {
  const isOverflowEnabled = enableOverflow && displayMode === 'inline-pipe'

  const { containerRef, visibleCount, hiddenCount, phase } = useOverflowList<HTMLDivElement>({
    itemCount: results.length,
    maxRows: 1,
  })

  useEffect(() => {
    onOverflowCountChange?.(isOverflowEnabled && phase !== 'measuring' ? hiddenCount : null)
  }, [hiddenCount, isOverflowEnabled, onOverflowCountChange, phase])

  const displayedCount = isOverflowEnabled && phase !== 'measuring'
    ? Math.min(visibleCount, results.length)
    : results.length

  return (
    <section className="quick-search__section">
      {results.length === 0
        ? <EmptyState />
        : (
            <div
              ref={isOverflowEnabled ? containerRef : undefined}
              className={clsx('quick-search__list', {
                'quick-search__list--inline-pipe': displayMode === 'inline-pipe',
                'quick-search__list--overflow-enabled': isOverflowEnabled,
              })}
            >
              {results.map((result, index) => {
                const isVisible = !isOverflowEnabled
                  || phase === 'measuring'
                  || index < visibleCount

                if (!isVisible)
                  return null

                return (
                  <div className="quick-search__list-entry" key={result.id}>
                    <HierarchyItem
                      query={query}
                      result={result}
                      displayMode={displayMode}
                      displayType={displayType}
                      isLastItem={index === displayedCount - 1}
                      onClick={() => onResultSelect(result)}
                    />
                  </div>
                )
              })}
            </div>
          )}
    </section>
  )
}
